<!--
/**
 * DmDocumentToolingComponent Template
 *
 * Displays documents assigned to a business object with selectable display modes
 * (chips, dropdown, list) and an integrated document preview area.
 * Optionally provides document upload functionality.
 *
 * @module DmModule
 * @selector mvs-dm-document-tooling
 */
-->

<div class="flex flex-column" [style.height]="height">

    <!-- Header Row -->
    <div class="flex align-items-center gap-2 p-2 surface-0 border-bottom-1 surface-border">
        <span class="font-bold text-lg">Dokumente</span>
        <span class="text-500">({{ documents.length }})</span>

        <div class="ml-auto flex align-items-center gap-1">
            <!-- Display mode toggle -->
            @if (showDisplayModeToggle && documents.length > 0) {
                <p-selectButton
                    [options]="displayModeOptions"
                    [(ngModel)]="displayMode"
                    optionLabel="icon"
                    optionValue="value"
                    [allowEmpty]="false">
                    <ng-template let-item pTemplate="item">
                        <i [class]="item.icon"></i>
                    </ng-template>
                </p-selectButton>
            }

            <!-- Upload button -->
            @if (showUpload) {
                <p-button
                    icon="fa-regular fa-upload"
                    [text]="true"
                    (onClick)="showUploadDialog = true">
                </p-button>
            }
        </div>
    </div>

    <!-- Document Selection Area -->
    @if (initialized && documents.length > 0) {
        <div class="p-2">

            <!-- Chips mode -->
            @if (displayMode === 'chips') {
                <chip-select
                    [options]="documents"
                    [mode]="'single'"
                    [selectedOption]="selectedDocument"
                    key="documentId"
                    label="documentName"
                    icon="documentTypeImage"
                    (onChangeSingleOption)="onDocumentChanged($event)">
                </chip-select>
            }

            <!-- Dropdown mode -->
            @if (displayMode === 'dropdown') {
                <p-select
                    [options]="documents"
                    [(ngModel)]="selectedDocument"
                    optionLabel="documentName"
                    placeholder="Dokument auswählen"
                    (onChange)="onDocumentChanged($event.value)"
                    class="w-full">
                </p-select>
            }

            <!-- List mode -->
            @if (displayMode === 'list') {
                <p-listbox
                    [options]="documents"
                    [(ngModel)]="selectedDocument"
                    optionLabel="documentName"
                    (onChange)="onDocumentChanged($event.value)"
                    [listStyle]="{'max-height': '12.5rem'}">
                    <ng-template let-doc pTemplate="item">
                        <div class="flex align-items-center gap-2">
                            @if (doc.documentTypeImage) {
                                <i [class]="doc.documentTypeImage"></i>
                            }
                            <span>{{ doc.documentName }}</span>
                            <span class="ml-auto text-500 text-sm">{{ doc.lastModified | date:'dd.MM.yyyy' }}</span>
                        </div>
                    </ng-template>
                </p-listbox>
            }
        </div>
    }

    <!-- Document Preview (takes remaining height) -->
    @if (selectedDocument) {
        <div class="flex-1 overflow-hidden p-2">
            <mvs-pdf-viewer
                [objectId]="selectedDocument.documentId"
                [documentMimeType]="selectedDocument.mimeType"
                [imageHeader]="true"
                [view]="'#view=FitH&navpanes=0'"
                class="w-full h-full">
            </mvs-pdf-viewer>
        </div>
    }

    <!-- Empty state -->
    @if (!selectedDocument && initialized && !busy) {
        <div class="flex-1 flex justify-content-center text-500 pt-8">
            <div class="text-center">
                <i class="fa-regular fa-file text-4xl mb-2"></i>
                <p class="mb-1">Keine Dokumente vorhanden</p>
                <p class="text-sm">Es wurden noch keine Dokumente zugewiesen.</p>
                @if (showUpload) {
                    <p-button
                        label="Dokument hochladen"
                        icon="fa-regular fa-upload"
                        [text]="true"
                        (onClick)="showUploadDialog = true"
                        class="mt-2">
                    </p-button>
                }
            </div>
        </div>
    }

    <!-- Loading state -->
    @if (busy) {
        <div class="flex-1 flex align-items-center justify-content-center">
            <p-progressSpinner [style]="{width: '2.5rem', height: '2.5rem'}"></p-progressSpinner>
        </div>
    }

</div>

<!-- Upload Dialog -->
<p-dialog
    [(visible)]="showUploadDialog"
    header="Dokument hochladen"
    [modal]="true"
    [style]="{width: '50vw'}">
    <mvs-dm-upload
        [objectIdentifier]="objectIdentifier"
        [documentTypeId]="uploadDocumentTypeIds?.length === 1 ? uploadDocumentTypeIds[0] : null"
        [isDocumentTypeSelectionMode]="uploadDocumentTypeIds?.length !== 1"
        (onDocumentUploaded)="handleDocumentUploaded($event)">
    </mvs-dm-upload>
</p-dialog>
